import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { faUnlock, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { SmartyStreetsService } from '../../../core/common/smart-streets.service';
import { SecurityService } from '../../../core/security/security.service';
import { SystemMessage, SystemMessageService } from '../../../core/system-message/system-message-service';
import { EditUserVm } from '../../../shared/generated/Administration/Models/User/EditUser/EditUserVm';
import { UserJsVm } from '../../../shared/generated/Administration/Models/User/JavaScript/UserJsVm';
import { GlobalVariablesService } from '../../../services/global-variables.service';

@Component({
    selector: 'pcg-user-associate-sites',
    templateUrl: './user-associate-sites.component.html',
    styleUrls: ['./user-associate-sites.component.scss'],
    animations: [
        trigger('visibilityChanged', [
            state('true', style({ opacity: 1 })),
            state('false', style({ opacity: 0 })),
            transition('* => *', animate('.3s'))
        ])
    ],
    standalone: false
})
export class UserAssociateSitesComponent implements OnInit, OnDestroy {

    formGroup = EditUserVm.Form;
    model = EditUserVm.GetModel(this.formGroup);
    allSites: string[] = [];
    associatedSites: string[] = [];
    sites: any[];
    user: UserJsVm;

    // Security variables
    canManageUsers = false;
    isCurrentUser = false;
    showInactive = false;
    showNewUserMessage = false;
    firstLoad = false;
    newUserParam = false;
    isCreatingNewUser = false;

    id: number;

    displayMessage: 'block' | 'none' = 'none';

    timeout: any
    
    faIconName = { faUnlock, faInfoCircle };	

    constructor(
        private route: ActivatedRoute
        , private httpClient: HttpClient
        , public router: Router 
        , private ms: SystemMessageService
        , public smartyStreetsService: SmartyStreetsService
        , private sec: SecurityService
        , private changeDetectorRef: ChangeDetectorRef
        , private gvs: GlobalVariablesService
    ) { }

    ngOnInit() {
        this.user = this.sec.getUser();
        this.addSecuritySubscription();
        this.addRouteSubscription();
        this.isCreatingNewUser = this.route.snapshot.queryParams.isCreatingNewUser === "true";
    }

    /** Populate 'Sites List' and 'Associated Sites' lists with sites associated to organization. */
    populateSites() {
        if (!this.formGroup.controls.organizationId.value) { this.sites = this.allSites = this.associatedSites = []; }
        else {
            this.httpClient.get('api/Select/Sites/?orgId=' + this.formGroup.controls.organizationId.value + '&showInactive=' + this.showInactive)
                .subscribe((items: any) => {
                    this.sites = items;
                    this.allSites = items.reduce((accum, current) => {
                        accum.push(current.text);
                        return accum;
                    }, []);
                    // Clear out selected sites not associated to the org.
                    this.associatedSites = this.associatedSites.filter(i => items.find(o => o.text === i));
                    // Populate Associated Sites list with sites already asscoiated to user and to selected org.
                    if (this.formGroup.value.sites)
                        this.associatedSites = this.formGroup.value.sites.filter((i: string) => items.find(o => o.text === i));
                }
            );
        }
    }

    /** Update the local dynamic security object when the security updates */
    addSecuritySubscription() {
        this.sec.security$.subscribe(() => {
            this.canManageUsers = this.sec.hasAccess(o => o.manageSiteUsers);
            this.sec.setControlAccess(this.formGroup?.controls?.permissionId, o => o.manageSiteUsers);
        });
    }

    /** Subscribe to route to pick up id changes and update page accordingly */
    addRouteSubscription() {        
        this.route.paramMap.subscribe(map => {
            // Allow user to view page if it is either their profile
            // or they have admin access to view other people's profiles
            const user = this.sec.getUser();
            this.id = parseInt(map.get('id')!, 10);
            let api = this.newUserParam === true 
                ? `api/Administration/Users/GetUser/${this.id}`
                : `api/Administration/Users/GetUser/${this.id}?isNewUser=${true}`;
            if (
                !this.canManageUsers 
                && this.id !== user?.id
            ) { this.router.navigateByUrl('/'); }
            this.isCurrentUser = this.id === this.sec.getUser()?.id;
            this.httpClient.get(api)
                .subscribe((model: any) => {
                    this.formGroup.patchValue(model);
                    if (model?.isActive === false) { this.showInactive = true; }
                    this.gvs.userName.set(model?.fullName);
                    this.populateSites();
                }
            );
        });
    }

    // used for the back button on the associate inventory page only for a new user
    handleBackAssociate() { this.router.navigateByUrl(`users/edit/details/${this.id}`); }

    handleFormGroup() {
        this.formGroup.controls.sites.setValue(this.associatedSites);
        this.formGroup.controls.siteIds.setValue(
            this.sites.reduce((accum, current) => {
                if (this.associatedSites?.includes(current.text)) { accum.push(current.value); }
                return accum;
            }, [])
        );
    }

    saveUser(msg: SystemMessage) {
        if (msg.value) {
            const model = this.formGroup.value;			
            this.id = model.userId;
            this.sec.setSecurity(undefined!, msg.value.userJsVm, msg.value.dynamicSecurity);
        }
        if (!msg.isSuccessful) { setTimeout(() => { this.changeDetectorRef.detectChanges(); }, 1); } 
        else {
            if (this.isCreatingNewUser) {
                this.showNewUserMessage = true;
                this.firstLoad = true;
                this.ms.settings.messageOpen = false; // stops the default system message from showing
                this.timeout = setTimeout(() => {
                    this.showNewUserMessage = false;
                    this.router.navigateByUrl("users/list")
                }, 6500);
            }
        }
    }

    /** Called on click of the reset password and welcome email buttons */
	resetPassword(
        isWelcomeEmail: boolean
        , isSystemMessage: boolean
    ) {
		
        this.httpClient.post(
            'api/Account/ForgotPassword'
            , {
                email: this.formGroup.value.email
                , isWelcomeEmail
            }
        ).subscribe((msg: any) => {
            if (msg.isSuccessful) {
                this.ms.setSystemMessage(`An email has been sent to the user with a ${isWelcomeEmail ? 'welcome' : 'reset password'} link.`);
            } else { this.ms.setSystemMessage('An error occurred when attempting to send the email.', 'error'); }
        });
		// timed out so that the user can see the confirmation system message before being redirected
		if (isSystemMessage) { setTimeout(() => { this.router.navigateByUrl("users/list") }, 500); }	
	}

    getCenteredStyles() {
		const mm = document?.body?.clientWidth;
		const mw = 650;
		return { minWidth: (mm < mw ? mm : mw) + 'px', left: (mm < mw ? 0 : (mm - mw) / 2) + 'px' };
	}

	closeSystemMessage() {
		this.showNewUserMessage = false;
		clearTimeout(this.timeout)
	}

    ngOnDestroy() { this.formGroup = null!; }
}